<template>
  <a-modal :visible="visible" :title="goods ? goods.materialName : '选择选项'"
           :maskClosable="false" width="480px"
           okText="加入点单" cancelText="取消"
           :okButtonProps="{ props: { disabled: !valid } }"
           @ok="handleOk" @cancel="$emit('cancel')">
    <div class="opt-body" v-if="goods">
      <!-- 参考价：最低零售价 + 加料加价（成交价以后端下单计价为准） -->
      <div class="opt-price">
        参考价：<span class="opt-price-num">¥{{ fmt(basePrice) }}</span>
        <span v-if="extraAmount > 0" class="opt-price-extra">+ 加料 ¥{{ fmt(extraAmount) }}</span>
      </div>
      <!-- 选项组：SINGLE 规格单选 / PRACTICE 做法单选 / EXTRA 加料多选（加价累加） -->
      <div class="opt-group" v-for="group in goods.options" :key="group.id">
        <div class="opt-group-head">
          <span class="opt-group-name">{{ group.name }}</span>
          <a-tag v-if="isRequired(group)" color="red">必选</a-tag>
          <span class="opt-group-type">{{ group.type === 'EXTRA' ? '多选' : '单选' }}</span>
        </div>
        <div class="opt-values">
          <div class="opt-value" v-for="v in group.values" :key="v.id"
               :class="{ active: isSelected(group, v) }" @click="toggle(group, v)">
            <span>{{ v.name }}</span>
            <span v-if="Number(v.addPrice) > 0" class="opt-add">+¥{{ fmt(v.addPrice) }}</span>
          </div>
        </div>
      </div>
      <div class="opt-row">
        <span class="opt-label">数量</span>
        <div class="qty-step">
          <a-button size="small" :disabled="quantity <= 1" @click="quantity > 1 && quantity--">-</a-button>
          <span class="qty-num">{{ quantity }}</span>
          <a-button size="small" @click="quantity < 999 && quantity++">+</a-button>
        </div>
      </div>
      <div class="opt-row">
        <span class="opt-label">备注</span>
        <a-input v-model="remark" placeholder="口味备注（选填）" style="flex: 1" />
      </div>
    </div>
  </a-modal>
</template>

<script>
/**
 * 菜品选项弹窗（OrderPanel 子组件）：
 * 数据源为菜单接口菜品行的 options 数组（组 type SINGLE/EXTRA/PRACTICE，值 addPrice 加价）。
 * 必选组（required='1'）未选不可确认；EXTRA 多选加价累加仅用于参考价展示，成交价由后端计价。
 */
export default {
  name: 'GoodsOptionsModal',
  props: {
    visible: { type: Boolean, default: false },
    goods: { type: Object, default: null }
  },
  data() {
    return {
      selected: {}, // {groupId: valueId | [valueId,...]}
      quantity: 1,
      remark: ''
    }
  },
  computed: {
    basePrice() {
      return Number((this.goods && this.goods.minPrice) || 0)
    },
    extraAmount() {
      let sum = 0
      const options = (this.goods && this.goods.options) || []
      options.forEach(group => {
        if (group.type !== 'EXTRA') return
        ((this.selected[group.id] || []).forEach(id => {
          const v = (group.values || []).find(item => item.id === id)
          if (v) sum += Number(v.addPrice || 0)
        }))
      })
      return sum
    },
    selectedValueIds() {
      const ids = []
      const options = (this.goods && this.goods.options) || []
      options.forEach(group => {
        const sel = this.selected[group.id]
        if (sel === undefined || sel === null) return
        if (Array.isArray(sel)) ids.push(...sel)
        else ids.push(sel)
      })
      return ids
    },
    optionText() {
      const names = []
      const options = (this.goods && this.goods.options) || []
      options.forEach(group => {
        const sel = this.selected[group.id]
        if (sel === undefined || sel === null) return
        const list = Array.isArray(sel) ? sel : [sel]
        list.forEach(id => {
          const v = (group.values || []).find(item => item.id === id)
          if (v) names.push(v.name)
        })
      })
      return names.join('/')
    },
    valid() {
      const options = (this.goods && this.goods.options) || []
      return options.every(group => {
        // 无可选值的组不阻断确认（防配置空组导致弹窗死锁）
        if (!group.values || !group.values.length) return true
        if (!this.isRequired(group)) return true
        const sel = this.selected[group.id]
        return Array.isArray(sel) ? sel.length > 0 : sel !== undefined && sel !== null
      })
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.selected = {}
        this.quantity = 1
        this.remark = ''
      }
    }
  },
  methods: {
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    isRequired(group) {
      return String(group.required) === '1'
    },
    isSelected(group, v) {
      const sel = this.selected[group.id]
      if (group.type === 'EXTRA') {
        return (sel || []).indexOf(v.id) > -1
      }
      return sel === v.id
    },
    toggle(group, v) {
      if (group.type === 'EXTRA') {
        const list = this.selected[group.id] ? [...this.selected[group.id]] : []
        const idx = list.indexOf(v.id)
        if (idx > -1) {
          list.splice(idx, 1)
        } else {
          list.push(v.id)
        }
        this.$set(this.selected, group.id, list)
      } else {
        // 单选：再点一次取消（必选组会在确认时校验）
        this.$set(this.selected, group.id, this.selected[group.id] === v.id ? null : v.id)
      }
    },
    handleOk() {
      if (!this.valid) {
        this.$message.warning('请先完成必选项选择')
        return
      }
      this.$emit('confirm', {
        quantity: this.quantity,
        remark: this.remark.trim(),
        optionValueIds: this.selectedValueIds,
        optionText: this.optionText,
        extraAmount: this.extraAmount
      })
    }
  }
}
</script>

<style scoped>
.opt-body {
  max-height: 60vh;
  overflow-y: auto;
}
.opt-price {
  font-size: 13px;
  color: #555;
  margin-bottom: 12px;
}
.opt-price-num {
  color: #e4393c;
  font-size: 18px;
  font-weight: 600;
}
.opt-price-extra {
  color: #e4393c;
  margin-left: 6px;
  font-size: 12px;
}
.opt-group {
  margin-bottom: 14px;
}
.opt-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.opt-group-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}
.opt-group-type {
  color: #999;
  font-size: 12px;
}
.opt-values {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.opt-value {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}
.opt-value.active {
  background: #0081ff;
  border-color: #0081ff;
  color: #fff;
}
.opt-add {
  font-size: 12px;
  color: #e4393c;
}
.opt-value.active .opt-add {
  color: #fff;
}
.opt-row {
  display: flex;
  align-items: center;
  margin-top: 12px;
  gap: 10px;
}
.opt-label {
  width: 36px;
  color: #555;
  font-size: 13px;
}
.qty-step {
  display: flex;
  align-items: center;
  gap: 8px;
}
.qty-num {
  min-width: 32px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
}
</style>
